import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { COURSE_API_URL as API_URL } from './lib/api-config';
import './PublicBooking.css';

function PublicBooking() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [participants, setParticipants] = useState([{ firstName: '', lastName: '', email: '', phone: '', company: '' }]);
  const [discountCode, setDiscountCode] = useState('');
  const [discountInfo, setDiscountInfo] = useState(null);
  const [comment, setComment] = useState('');
  const [consentGiven, setConsentGiven] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => { fetchCourses(); }, []);

  const fetchCourses = async () => {
    try {
      const response = await axios.get(API_URL + '/public/courses');
      setCourses(response.data.data);
      setLoading(false);
    } catch (err) {
      setError('Kunde inte hämta kurser');
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('sv-SE', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('sv-SE', { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(price);
  };

  const handleSelectCourse = (course) => {
    setSelectedCourse(course);
    setBookingResult(null);
    setError(null);
    setDiscountInfo(null);
  };

  const handleAddParticipant = () => {
    if (participants.length < 10) {
      setParticipants([...participants, { firstName: '', lastName: '', email: '', phone: '', company: '' }]);
    }
  };

  const handleRemoveParticipant = (index) => {
    if (participants.length > 1) {
      setParticipants(participants.filter((_, i) => i !== index));
    }
  };

  const handleParticipantChange = (index, field, value) => {
    const newParticipants = [...participants];
    newParticipants[index][field] = value;
    setParticipants(newParticipants);
  };

  const validateDiscount = async () => {
    if (!discountCode.trim()) return;
    try {
      const response = await axios.post(API_URL + '/public/validate-discount', {
        code: discountCode, courseInstanceId: selectedCourse.instanceId
      });
      if (response.data.success) {
        setDiscountInfo(response.data.data);
        setError(null);
      } else {
        setDiscountInfo(null);
        setError(response.data.error);
      }
    } catch (err) {
      setDiscountInfo(null);
      setError('Kunde inte validera rabattkod');
    }
  };

  const calculateTotal = () => {
    let price = selectedCourse.price;
    if (discountInfo) {
      if (discountInfo.discountType === 'percent') {
        price = price * (1 - discountInfo.discountValue / 100);
      } else {
        price = Math.max(0, price - discountInfo.discountValue);
      }
    }
    return price * participants.length;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    if (!consentGiven) {
      setError('Du måste godkänna integritetspolicyn för att boka');
      setSubmitting(false);
      return;
    }
    const validParticipants = participants.filter(p => p.firstName && p.lastName && p.email);
    if (validParticipants.length === 0) {
      setError('Fyll i uppgifter för minst en deltagare');
      setSubmitting(false);
      return;
    }
    try {
      const response = await axios.post(API_URL + '/public/book', {
        courseInstanceId: selectedCourse.instanceId,
        participants: validParticipants,
        discountCode: discountCode || null,
        comment: comment || null
      });
      if (response.data.success) {
        setBookingResult(response.data);
        setSelectedCourse(null);
        setParticipants([{ firstName: '', lastName: '', email: '', phone: '', company: '' }]);
        setDiscountCode('');
        setDiscountInfo(null);
        setComment('');
        setConsentGiven(false);
      } else {
        setError(response.data.error || 'Bokningen misslyckades');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Något gick fel vid bokningen');
    }
    setSubmitting(false);
  };

  if (loading) return <div className="booking-loading">Laddar kurser...</div>;

  if (bookingResult) {
    return (
      <div className="booking-container">
        <div className="booking-success">
          <h2>Tack för din bokning!</h2>
          <p>{bookingResult.message}</p>
          <div className="booking-summary">
            <p><strong>Totalt:</strong> {formatPrice(bookingResult.data.totalPrice)} exkl. moms</p>
            <p><strong>Antal deltagare:</strong> {bookingResult.data.bookings.length}</p>
            {bookingResult.data.waitlistCount > 0 && (
              <p className="waitlist-note">{bookingResult.data.waitlistCount} deltagare placerades på väntelista.</p>
            )}
          </div>
          <p className="confirmation-note">Bokningsbekräftelse skickas till alla deltagares e-postadresser.</p>
          <button className="btn-primary" onClick={() => { setBookingResult(null); fetchCourses(); }}>
            Boka fler kurser
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="booking-container">
      <header className="booking-header">
        <h1>Boka kurs</h1>
        <p className="cancellation-policy">Kostnadsfri avbokning fram till 14 dagar före kursstart</p>
      </header>
      {error && <div className="error-message">{error}</div>}
      {!selectedCourse ? (
        <div className="course-list">
          <h2>Välj kurs</h2>
          {courses.length === 0 ? (
            <p className="no-courses">Inga kurser tillgängliga just nu</p>
          ) : (
            courses.map(course => (
              <div key={course.instanceId} className={"course-card" + (course.isFull ? " full" : "")}>
                <div className="course-info">
                  <h3>{course.infoPageUrl ? <a href={course.infoPageUrl} target="_blank" rel="noopener noreferrer">{course.courseName}</a> : course.courseName}</h3>
                  <p className="course-date">{formatDate(course.startDate)}</p>
                  <p className="course-location">{course.city} - {course.location}</p>
                  <p className="course-time">{course.startTime} - {course.endTime}</p>
                  {course.instructorName && <p className="course-instructor">Lärare: {course.instructorName}</p>}
                </div>
                <div className="course-price">
                  {course.isEarlyBird ? (
                    <>
                      <span className="early-bird-badge">Early Bird</span>
                      <span className="price-current">{formatPrice(course.price)}</span>
                      <span className="price-original">{formatPrice(course.originalPrice)}</span>
                    </>
                  ) : (
                    <span className="price-current">{formatPrice(course.price)}</span>
                  )}
                  <span className="price-note">exkl. moms</span>
                </div>
                <div className="course-action">
                  {course.isFull ? (
                    <button className="btn-waitlist" onClick={() => handleSelectCourse(course)}>Anmäl till väntelista</button>
                  ) : (
                    <button className="btn-primary" onClick={() => handleSelectCourse(course)}>Boka nu</button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      ) : (
        <div className="booking-form-container">
          <button className="btn-back" onClick={() => setSelectedCourse(null)}>Tillbaka till kurslistan</button>
          <div className="selected-course-summary">
            <h2>{selectedCourse.courseName}</h2>
            <p>{formatDate(selectedCourse.startDate)} | {selectedCourse.city}</p>
            <p className="price">{formatPrice(selectedCourse.price)} exkl. moms per deltagare</p>
            {selectedCourse.isFull && <p className="full-notice">Kursen är fullbokad - du anmäler till väntelista</p>}
          </div>
          <form onSubmit={handleSubmit} className="booking-form">
            <h3>Deltagare</h3>
            {participants.map((participant, index) => (
              <div key={index} className="participant-row">
                <div className="participant-header">
                  <span>Deltagare {index + 1}</span>
                  {participants.length > 1 && <button type="button" className="btn-remove" onClick={() => handleRemoveParticipant(index)}>Ta bort</button>}
                </div>
                <div className="participant-fields">
                  <input type="text" placeholder="Förnamn *" value={participant.firstName} onChange={(e) => handleParticipantChange(index, 'firstName', e.target.value)} required />
                  <input type="text" placeholder="Efternamn *" value={participant.lastName} onChange={(e) => handleParticipantChange(index, 'lastName', e.target.value)} required />
                  <input type="email" placeholder="E-post *" value={participant.email} onChange={(e) => handleParticipantChange(index, 'email', e.target.value)} required />
                  <input type="tel" placeholder="Telefon" value={participant.phone} onChange={(e) => handleParticipantChange(index, 'phone', e.target.value)} />
                  <input type="text" placeholder="Företag" value={participant.company} onChange={(e) => handleParticipantChange(index, 'company', e.target.value)} />
                </div>
              </div>
            ))}
            <button type="button" className="btn-add-participant" onClick={handleAddParticipant}>+ Lägg till deltagare</button>
            <div className="discount-section">
              <h3>Rabattkod</h3>
              <div className="discount-input">
                <input type="text" placeholder="Ange rabattkod" value={discountCode} onChange={(e) => setDiscountCode(e.target.value.toUpperCase())} maxLength={9} />
                <button type="button" onClick={validateDiscount}>Använd</button>
              </div>
              {discountInfo && <p className="discount-applied">{discountInfo.description} tillämpas!</p>}
            </div>
            <div className="comment-section">
              <h3>Kommentar</h3>
              <textarea placeholder="Eventuell kommentar..." value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
            </div>
            <div className="booking-total">
              <span>Totalt ({participants.length} deltagare):</span>
              <span className="total-price">{formatPrice(calculateTotal())} exkl. moms</span>
            </div>
            <div className="consent-section">
              <label className="consent-label">
                <input
                  type="checkbox"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  required
                />
                <span>
                  Jag godkänner att Projektledarpodden sparar mina uppgifter för att administrera min kursbokning.
                  Läs vår <a href="/integritetspolicy" target="_blank" rel="noopener noreferrer">integritetspolicy</a>.
                </span>
              </label>
            </div>
            <button type="submit" className="btn-submit" disabled={submitting || !consentGiven}>
              {submitting ? 'Bokar...' : (selectedCourse.isFull ? 'Anmäl till väntelista' : 'Bekräfta bokning')}
            </button>
          </form>
        </div>
      )}
      <footer className="booking-footer">
        <p>Projektledarpodden | Ejbe Vision AB</p>
      </footer>
    </div>
  );
}

export default PublicBooking;
